import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { apiClient } from '@services/api';
import { connect } from 'react-redux';
import BreadCrumbs from '@components/Breadcrumbs';
import Button from '@components/Button';
import Icon from '@material-ui/core/Icon';
import CircularProgress from '@material-ui/core/CircularProgress';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';

// Carinhas de avaliação
const RATINGS = [
  { value: 1, emoji: '😠', label: 'Muito insatisfeito' },
  { value: 2, emoji: '😕', label: 'Insatisfeito' },
  { value: 3, emoji: '😐', label: 'Regular' },
  { value: 4, emoji: '🙂', label: 'Satisfeito' },
  { value: 5, emoji: '😄', label: 'Muito satisfeito' },
];

const PRIORITY_COLORS = {
  urgent: '#ef4444',
  high:   '#f59e0b',
  normal: '#38bdf8',
  low:    '#64748b',
};

const Show = (props) => {
  const { ticket_id } = useParams();
  const history = useHistory();
  const messagesEndRef = useRef(null);

  const [ticket, setTicket]           = useState(null);
  const [loading, setLoading]         = useState(true);
  const [notFound, setNotFound]       = useState(false);
  const [replyBody, setReplyBody]     = useState('');
  const [isInternal, setIsInternal]   = useState(false);
  const [markResolved, setMarkResolved] = useState(false);
  const [sending, setSending]         = useState(false);
  const [sendError, setSendError]     = useState('');
  const [replyFiles, setReplyFiles]   = useState([]);
  const replyFileRef                  = useRef();

  // Consultor
  const [users, setUsers]                   = useState([]);
  const [consultantSearch, setConsultantSearch] = useState('');
  const [showConsultantPicker, setShowConsultantPicker] = useState(false);
  const [savingConsultant, setSavingConsultant] = useState(false);

  // Rating
  const [ratingValue, setRatingValue]   = useState(null);
  const [ratingComment, setRatingComment] = useState('');
  const [ratingError, setRatingError]   = useState('');
  const [ratingSuccess, setRatingSuccess] = useState(false);
  const [submittingRating, setSubmittingRating] = useState(false);

  // Permissões
  const adminRoles = ['administrador', 'gerente', 'logistica', 'supervisor'];
  const userPermission = props?.user?.data?.permission || '';
  const isAdmin = adminRoles.includes(userPermission.toLowerCase());
  const userId = props?.user?.data?.id;

  const loadTicket = () => {
    apiClient.httpInstance.get(`/tickets/${ticket_id}`).then(res => {
      if (res.status === 200) {
        setTicket(res.data.data);
        setLoading(false);
      }
    }).catch(() => {
      setLoading(false);
      setNotFound(true);
    });
  };

  const loadUsers = () => {
    apiClient.httpInstance.get('/users?seller=true&status=true&pageSize=500').then(res => {
      if (res.status === 200) {
        const list = res.data.data || res.data || [];
        setUsers(Array.isArray(list) ? list : []);
      }
    }).catch(() => {});
  };

  const handleAssignConsultant = (consultantId) => {
    setSavingConsultant(true);
    apiClient.httpInstance
      .put(`/tickets/${ticket_id}/consultant`, { consultant_id: consultantId })
      .then(res => {
        if (res.status === 200) {
          setTicket(res.data.data);
          setShowConsultantPicker(false);
          setConsultantSearch('');
        }
      })
      .finally(() => setSavingConsultant(false));
  };

  useEffect(() => {
    props.setShowMenu(false);
    loadTicket();
    loadUsers();
  }, [ticket_id]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ticket?.messages]);

  const handleReplyFiles = (e) => {
    const chosen = Array.from(e.target.files || []);
    const valid  = chosen.filter(f => f.size <= 8 * 1024 * 1024);
    setReplyFiles(prev => [...prev, ...valid].slice(0, 5));
    if (replyFileRef.current) replyFileRef.current.value = '';
  };

  const handleReply = () => {
    if (!replyBody.trim()) return;
    setSending(true);
    setSendError('');

    const formData = new FormData();
    formData.append('body', replyBody);
    formData.append('is_internal', isInternal ? 1 : 0);
    formData.append('mark_resolved', markResolved ? 1 : 0);
    replyFiles.forEach((f, i) => formData.append(`files[${i}]`, f));

    apiClient.httpInstance
      .post(`/tickets/${ticket_id}/reply`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(res => {
        if (res.status === 200) {
          setTicket(res.data.data);
          setReplyBody('');
          setIsInternal(false);
          setMarkResolved(false);
          setReplyFiles([]);
        }
      })
      .catch(() => setSendError('Erro ao enviar. Tente novamente.'))
      .finally(() => setSending(false));
  };

  const handleTransition = (action, extra = {}) => {
    apiClient.httpInstance
      .post(`/tickets/${ticket_id}/transition`, { action, ...extra })
      .then(res => {
        if (res.status === 200) setTicket(res.data.data);
      });
  };

  const handleRate = () => {
    if (!ratingValue) return;
    if (ratingValue <= 2 && !ratingComment.trim()) {
      setRatingError('Informe o que aconteceu (obrigatório para notas 1 e 2).');
      return;
    }
    setSubmittingRating(true);
    setRatingError('');
    apiClient.httpInstance
      .post(`/tickets/${ticket_id}/rate`, {
        rating: ratingValue,
        comment: ratingComment,
      })
      .then(res => {
        if (res.status === 200) {
          setTicket(res.data.data);
          setRatingSuccess(true);
        }
      })
      .catch(() => setRatingError('Erro ao enviar avaliação.'))
      .finally(() => setSubmittingRating(false));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <CircularProgress style={{ color: props.theme.color }} />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-7" style={{ color: '#8ba5c4' }}>
        <Icon style={{ fontSize: '48px', marginBottom: '12px', color: '#4a6080' }}>confirmation_number</Icon>
        <p style={{ fontSize: '16px', marginBottom: '16px' }}>Ticket não encontrado ou sem permissão de acesso.</p>
        <button
          onClick={() => history.push('/admin/tickets')}
          style={{ background: '#172B4D', color: '#38bdf8', border: '1px solid #1e3a5f', borderRadius: '8px', padding: '8px 20px', cursor: 'pointer', fontSize: '14px' }}
        >
          ← Voltar aos tickets
        </button>
      </div>
    );
  }

  const breadcrumbs = [
    { title: 'Tickets', active: true, route: '/admin/tickets' },
    { title: `#${ticket.id} — ${ticket.subject}` },
  ];

  const isClosed      = ticket.status === 'closed';
  const isResolved    = ticket.status === 'resolved';
  const isOpener      = Number(ticket.opener_id) === Number(userId);
  const isAssignee    = Number(ticket.assigned_to) === Number(userId);
  const isConsultant  = ticket.consultant_id === userId;
  const canReply      = !isClosed && !(isResolved && !ticket.can_reopen);
  const canRate       = ticket.can_be_rated && isOpener;
  const isRated       = ticket.rating !== null;
  const daysLeft      = ticket.reopen_days_left ?? 0;
  const canSeeInternal = isAssignee || isConsultant;

  const filteredUsers = users.filter(u => {
    const uid = u.user_id || u.id;
    return Number(uid) !== Number(userId) &&
      Number(uid) !== Number(ticket.assigned_to) &&
      (!consultantSearch || u.name?.toLowerCase().includes(consultantSearch.toLowerCase()));
  });

  return (
    <>
      <BreadCrumbs items={breadcrumbs} />

      <div className="px-7 pb-10 max-w-4xl">

        {/* ── Cabeçalho do ticket ── */}
        <div
          className="mt-4 p-5 rounded-xl"
          style={{ background: '#0f2340', border: '1px solid #1e3a5f' }}
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-semibold text-white">{ticket.subject}</h2>
              <div className="flex items-center flex-wrap gap-3 mt-2 text-sm" style={{ color: '#94a3b8' }}>
                <span>#{ticket.id}</span>
                <span>{ticket.category_label}</span>
                <span>Aberto por <strong className="text-white">{ticket.opener_name}</strong></span>
                <span>{ticket.created_at}</span>
                {ticket.assignee_name && (
                  <span>→ <strong className="text-white">{ticket.assignee_name}</strong></span>
                )}
                {ticket.consultant_name && (
                  <span style={{ color: '#a78bfa' }}>
                    <Icon style={{ fontSize: 13, verticalAlign: 'middle', marginRight: 3 }}>support_agent</Icon>
                    Consultor: <strong style={{ color: '#c4b5fd' }}>{ticket.consultant_name}</strong>
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Badge prioridade */}
              <span
                className="text-xs px-2 py-1 rounded-full font-medium"
                style={{
                  background: (PRIORITY_COLORS[ticket.priority] || '#64748b') + '22',
                  color: PRIORITY_COLORS[ticket.priority] || '#64748b',
                  border: `1px solid ${(PRIORITY_COLORS[ticket.priority] || '#64748b')}44`,
                }}
              >
                {ticket.priority_label}
              </span>
              {/* Badge status */}
              <span
                className="text-xs px-2 py-1 rounded-full font-medium"
                style={{
                  background: ticket.status_color + '22',
                  color: ticket.status_color,
                  border: `1px solid ${ticket.status_color}44`,
                }}
              >
                {ticket.status_label}
              </span>
            </div>
          </div>

          {/* Ações admin */}
          {isAdmin && (
            <div className="flex gap-2 mt-4 flex-wrap">
              {!ticket.assigned_to && (
                <ActionButton
                  icon="person_add"
                  label="Assumir"
                  color={props.theme.color}
                  onClick={() => handleTransition('assign_me')}
                />
              )}
              {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
                <ActionButton
                  icon="check_circle"
                  label="Resolver"
                  color="#22c55e"
                  onClick={() => handleTransition('set_status', { status: 'resolved' })}
                />
              )}
              {ticket.status === 'resolved' && (
                <ActionButton
                  icon="lock"
                  label="Fechar"
                  color="#64748b"
                  onClick={() => handleTransition('set_status', { status: 'closed' })}
                />
              )}
              {ticket.can_reopen && (
                <ActionButton
                  icon="refresh"
                  label="Reabrir"
                  color="#f59e0b"
                  onClick={() => handleTransition('reopen')}
                />
              )}
              {/* Consultor — só o assignee pode gerenciar */}
              {isAssignee && !isClosed && (
                <ActionButton
                  icon="support_agent"
                  label={ticket.consultant_name ? 'Trocar consultor' : 'Pedir consultor'}
                  color="#a78bfa"
                  onClick={() => { setShowConsultantPicker(p => !p); setConsultantSearch(''); }}
                />
              )}
              {isAssignee && ticket.consultant_id && !isClosed && (
                <ActionButton
                  icon="person_remove"
                  label="Remover consultor"
                  color="#64748b"
                  onClick={() => handleAssignConsultant(null)}
                />
              )}
            </div>
          )}

          {/* Picker de consultor */}
          {showConsultantPicker && (
            <div
              className="mt-3 p-3 rounded-xl"
              style={{ background: '#0a1929', border: '1px solid #6366f144' }}
            >
              <p className="text-xs mb-2" style={{ color: '#a78bfa' }}>
                Escolha um colega para te apoiar neste ticket (invisível para quem abriu):
              </p>
              <input
                type="text"
                placeholder="Buscar por nome..."
                value={consultantSearch}
                onChange={e => setConsultantSearch(e.target.value)}
                style={{
                  width: '100%', background: '#0f2340', border: '1px solid #1e3a5f',
                  borderRadius: 8, padding: '6px 10px', color: '#e2e8f0', fontSize: 13, outline: 'none',
                }}
              />
              <div className="mt-2 space-y-1" style={{ maxHeight: 180, overflowY: 'auto' }}>
                {filteredUsers.length === 0 && (
                  <p className="text-xs" style={{ color: '#475569' }}>Nenhum usuário encontrado.</p>
                )}
                {filteredUsers.map(u => (
                  <button
                    key={u.user_id || u.id}
                    onClick={() => handleAssignConsultant(u.user_id || u.id)}
                    disabled={savingConsultant}
                    className="w-full text-left px-3 py-2 rounded-lg text-sm transition-opacity hover:opacity-80"
                    style={{ background: '#172B4D', color: '#e2e8f0', border: '1px solid #1e3a5f', cursor: 'pointer' }}
                  >
                    {u.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Banner de prazo de reabertura (visível para todos) */}
          {isResolved && (
            <div
              className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg text-xs"
              style={ticket.can_reopen
                ? { background: '#052e1622', border: '1px solid #22c55e44', color: '#4ade80' }
                : { background: '#7f1d1d22', border: '1px solid #ef444444', color: '#f87171' }
              }
            >
              <Icon style={{ fontSize: 15 }}>
                {ticket.can_reopen ? 'schedule' : 'lock'}
              </Icon>
              {ticket.can_reopen
                ? `Resolvido — pode ser reaberto por mais ${daysLeft} dia${daysLeft !== 1 ? 's' : ''}`
                : 'Prazo de reabertura expirado. Abra um novo ticket se precisar continuar.'
              }
            </div>
          )}
        </div>

        {/* ── Thread de mensagens ── */}
        <div className="mt-4 space-y-3">
          {(ticket.messages || []).map(m => (
            <MessageBubble
              key={m.id}
              message={m}
              isAdmin={isAdmin}
              themeColor={props.theme.color}
            />
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* ── Aviso de ticket fechado/prazo expirado ── */}
        {isClosed && (
          <div
            className="mt-4 flex items-center gap-2 px-4 py-3 rounded-xl text-sm"
            style={{ background: '#0f2340', border: '1px solid #1e3a5f', color: '#64748b' }}
          >
            <Icon style={{ fontSize: 18 }}>lock</Icon>
            Ticket fechado. Abra um novo ticket se precisar de suporte.
          </div>
        )}
        {!isClosed && isResolved && !ticket.can_reopen && (
          <div
            className="mt-4 flex items-center gap-2 px-4 py-3 rounded-xl text-sm"
            style={{ background: '#7f1d1d22', border: '1px solid #ef444444', color: '#f87171' }}
          >
            <Icon style={{ fontSize: 18 }}>schedule</Icon>
            Prazo de reabertura expirado. Abra um novo ticket se precisar continuar.
          </div>
        )}

        {/* ── Formulário de resposta ── */}
        {canReply && (
          <div
            className="mt-4 p-4 rounded-xl"
            style={{ background: '#0f2340', border: '1px solid #1e3a5f' }}
          >
            <textarea
              className="w-full text-sm rounded-lg p-3 resize-none"
              rows={4}
              placeholder="Escreva sua resposta..."
              value={replyBody}
              onChange={e => setReplyBody(e.target.value)}
              style={{
                background: '#0a1929',
                border: '1px solid #1e3a5f',
                color: '#e2e8f0',
                outline: 'none',
              }}
            />

            {/* Opções admin/consultor */}
            {canSeeInternal && (
              <div className="flex items-center gap-4 mt-2">
                <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: '#94a3b8' }}>
                  <input
                    type="checkbox"
                    checked={isInternal}
                    onChange={e => setIsInternal(e.target.checked)}
                  />
                  Nota interna (só responsável e consultor veem)
                </label>
                {isAssignee && (
                  <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: '#94a3b8' }}>
                    <input
                      type="checkbox"
                      checked={markResolved}
                      onChange={e => setMarkResolved(e.target.checked)}
                    />
                    Marcar como resolvido ao enviar
                  </label>
                )}
              </div>
            )}

            {/* Anexos na resposta */}
            <div className="mt-3">
              {replyFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {replyFiles.map((f, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg"
                      style={{ background: '#0a1929', color: '#94a3b8', border: '1px solid #1e3a5f' }}
                    >
                      <Icon style={{ fontSize: 14, color: '#38bdf8' }}>
                        {f.type.startsWith('image/') ? 'image' : 'attach_file'}
                      </Icon>
                      <span style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {f.name}
                      </span>
                      <button
                        onClick={() => setReplyFiles(prev => prev.filter((_, j) => j !== i))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 0, lineHeight: 1 }}
                      >
                        <Icon style={{ fontSize: 13 }}>close</Icon>
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {replyFiles.length < 5 && (
                <button
                  onClick={() => replyFileRef.current?.click()}
                  className="flex items-center gap-1 text-xs"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569', padding: 0 }}
                >
                  <Icon style={{ fontSize: 15 }}>attach_file</Icon>
                  Anexar arquivo
                </button>
              )}
              <input
                ref={replyFileRef}
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx,.txt"
                style={{ display: 'none' }}
                onChange={handleReplyFiles}
              />
            </div>

            {sendError && <p className="text-xs mt-2" style={{ color: '#ef4444' }}>{sendError}</p>}

            <div className="flex justify-end mt-3">
              <Button
                textcolor="#fff"
                padding="6px 20px"
                margintop="0px"
                width="140"
                onClick={handleReply}
                customcolor={isInternal ? '#6366f1' : props.theme.color}
                disabled={sending || !replyBody.trim()}
              >
                {sending
                  ? <CircularProgress size={16} style={{ color: '#fff' }} />
                  : <><Icon className="mr-2" style={{ fontSize: 16 }}>send</Icon>{isInternal ? 'Salvar nota' : 'Responder'}</>
                }
              </Button>
            </div>
          </div>
        )}

        {/* ── Avaliação com carinhas ── */}
        {canRate && !ratingSuccess && (
          <RatingSection
            theme={props.theme}
            ratingValue={ratingValue}
            setRatingValue={setRatingValue}
            ratingComment={ratingComment}
            setRatingComment={setRatingComment}
            ratingError={ratingError}
            submittingRating={submittingRating}
            onSubmit={handleRate}
          />
        )}

        {/* Avaliação já enviada */}
        {(isRated || ratingSuccess) && (
          <div
            className="mt-4 p-4 rounded-xl flex items-center gap-4"
            style={{ background: '#0f2340', border: '1px solid #1e3a5f' }}
          >
            <span style={{ fontSize: 36 }}>{ticket.rating_emoji || RATINGS.find(r => r.value === ratingValue)?.emoji}</span>
            <div>
              <p className="text-sm text-white font-medium">{ticket.rating_label || 'Avaliação registrada'}</p>
              {ticket.rating_comment && (
                <p className="text-xs mt-1" style={{ color: '#94a3b8' }}>{ticket.rating_comment}</p>
              )}
            </div>
          </div>
        )}

      </div>
    </>
  );
};

// ── Sub-componentes ────────────────────────────────────────────

const MessageBubble = ({ message: m, isAdmin, themeColor }) => {
  const isInternal = m.is_internal;
  const isSupportMsg = m.is_admin;

  return (
    <div
      className="p-4 rounded-xl"
      style={{
        background: isInternal
          ? '#1a1040'
          : isSupportMsg ? '#0a1f3d' : '#0f2340',
        border: isInternal
          ? '1px dashed #6366f1'
          : isSupportMsg
            ? `1px solid ${themeColor}44`
            : '1px solid #1e3a5f',
        marginLeft: isSupportMsg ? '0' : '0',
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ color: isSupportMsg ? themeColor : '#e2e8f0' }}>
            {m.author_name}
          </span>
          {isSupportMsg && (
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: themeColor + '22', color: themeColor }}>
              Suporte
            </span>
          )}
          {isInternal && isAdmin && (
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#6366f122', color: '#a78bfa' }}>
              Nota interna
            </span>
          )}
        </div>
        <span className="text-xs" style={{ color: '#64748b' }}>{m.created_at}</span>
      </div>
      <p className="text-sm whitespace-pre-wrap" style={{ color: '#cbd5e1' }}>{m.body}</p>

      {/* Anexos da mensagem */}
      {m.attachments && m.attachments.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {m.attachments.map(att => (
            att.is_image ? (
              <a key={att.id} href={att.url} target="_blank" rel="noopener noreferrer">
                <img
                  src={att.url}
                  alt={att.file_name}
                  style={{
                    maxWidth: 200, maxHeight: 140, borderRadius: 8,
                    border: '1px solid #1e3a5f', objectFit: 'cover', cursor: 'pointer',
                  }}
                />
              </a>
            ) : (
              <a
                key={att.id}
                href={att.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg"
                style={{
                  background: '#0a1929', color: '#38bdf8',
                  border: '1px solid #1e3a5f', textDecoration: 'none',
                }}
              >
                <Icon style={{ fontSize: 15 }}>attach_file</Icon>
                <span style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {att.file_name}
                </span>
                <span style={{ color: '#475569', flexShrink: 0 }}>{att.file_size}</span>
              </a>
            )
          ))}
        </div>
      )}
    </div>
  );
};

const ActionButton = ({ icon, label, color, onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-opacity hover:opacity-80"
    style={{ background: color + '22', color, border: `1px solid ${color}44`, cursor: 'pointer' }}
  >
    <Icon style={{ fontSize: 14 }}>{icon}</Icon>
    {label}
  </button>
);

const RatingSection = ({
  theme, ratingValue, setRatingValue,
  ratingComment, setRatingComment,
  ratingError, submittingRating, onSubmit
}) => (
  <div
    className="mt-6 p-5 rounded-xl"
    style={{ background: '#0f2340', border: `1px solid ${theme.color}44` }}
  >
    <p className="text-sm font-medium text-white mb-1">Como foi o atendimento?</p>
    <p className="text-xs mb-4" style={{ color: '#94a3b8' }}>
      Avalie o chamado para nos ajudar a melhorar.
    </p>

    {/* Carinhas */}
    <div className="flex gap-3 mb-4">
      {RATINGS.map(r => (
        <button
          key={r.value}
          onClick={() => setRatingValue(r.value)}
          title={r.label}
          className="flex flex-col items-center gap-1 p-2 rounded-xl transition-all"
          style={{
            background: ratingValue === r.value ? theme.color + '33' : 'transparent',
            border: ratingValue === r.value ? `2px solid ${theme.color}` : '2px solid #1e3a5f',
            cursor: 'pointer',
            minWidth: 56,
          }}
        >
          <span style={{ fontSize: 28 }}>{r.emoji}</span>
          <span className="text-xs" style={{ color: ratingValue === r.value ? '#fff' : '#64748b' }}>
            {r.value}
          </span>
        </button>
      ))}
    </div>

    {/* Comentário */}
    <textarea
      className="w-full text-sm rounded-lg p-3 resize-none"
      rows={3}
      placeholder={
        ratingValue !== null && ratingValue <= 2
          ? 'Obrigatório: o que aconteceu?'
          : 'Comentário opcional...'
      }
      value={ratingComment}
      onChange={e => setRatingComment(e.target.value)}
      style={{
        background: '#0a1929',
        border: '1px solid #1e3a5f',
        color: '#e2e8f0',
        outline: 'none',
      }}
    />

    {ratingError && (
      <p className="text-xs mt-1" style={{ color: '#ef4444' }}>{ratingError}</p>
    )}

    <div className="flex justify-end mt-3">
      <Button
        textcolor="#fff"
        padding="6px 20px"
        margintop="0px"
        width="160"
        onClick={onSubmit}
        customcolor={theme.color}
        disabled={submittingRating || !ratingValue}
      >
        {submittingRating
          ? <CircularProgress size={16} style={{ color: '#fff' }} />
          : <><Icon className="mr-2" style={{ fontSize: 16 }}>star</Icon>Enviar avaliação</>
        }
      </Button>
    </div>
  </div>
);

const mapStateToProps = state => ({ user: state.user });
export default connect(mapStateToProps)(Show);
